import React from 'react';
import PropTypes from 'prop-types';

import colors from '../../../constants/Colors';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import { currencyFormatter, currencyParser } from '../../../helpers/inputParsers';
import { doesNumberExist } from '../../../helpers/helpers';
import { EQUIPMENT_CATEGORY_ID } from '../../../helpers/costcodeHelpers';

const WAGE_FIELDS = [
  { key: 'hourlyWage', label: 'Hourly Wage' },
  { key: 'dailyWage', label: 'Daily Wage' },
  { key: 'wageAdjustment', label: 'Wage Adjustment' },
  { key: 'wageMultiplier', label: 'Wage Multiplier' },
  { key: 'hourlyBillingRate', label: 'Hourly Billing Rate' },
  { key: 'dailyBillingRate', label: 'Daily Billing Rate' },
];

const INPUT_OPTIONS = {
  formatter: currencyFormatter,
  parser: currencyParser,
};

const getDisabledState = (key, wageValues, categoryIds) => {
  const {
    hourlyWage,
    dailyWage,
    wageAdjustment,
    wageMultiplier,
    hourlyBillingRate,
    dailyBillingRate,
  } = wageValues;

  const hasEquipment = categoryIds?.includes(EQUIPMENT_CATEGORY_ID);

  switch (key) {
    case 'hourlyWage':
      return doesNumberExist(wageMultiplier)
      || doesNumberExist(wageAdjustment)
      || (doesNumberExist(dailyWage) && !hasEquipment);
    case 'dailyWage':
      return doesNumberExist(wageMultiplier)
      || doesNumberExist(wageAdjustment)
      || (doesNumberExist(hourlyWage) && !hasEquipment);
    case 'wageAdjustment':
      return doesNumberExist(wageMultiplier)
        || doesNumberExist(hourlyWage)
        || doesNumberExist(dailyWage);
    case 'wageMultiplier':
      return doesNumberExist(hourlyWage)
        || doesNumberExist(dailyWage)
        || doesNumberExist(wageAdjustment);
    case 'hourlyBillingRate':
      return doesNumberExist(dailyBillingRate);
    case 'dailyBillingRate':
      return doesNumberExist(hourlyBillingRate);
    default:
      return false;
  }
};

export default function WageConfigurationForm({ record, wageValues, onWageChange }) {
  return (
    <div
      style={{
        padding: '16px 24px',
        backgroundColor: colors.GRAY_50,
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 16,
        }}
      >
        {WAGE_FIELDS.map(({ key, label }) => {
          const isDisabled = getDisabledState(key, wageValues, record.categoryIds);
          const isCurrency = key !== 'wageMultiplier';
          return (
            <div key={key}>
              <div className="budget-tracking-wage-configure-label">
                {label}
              </div>
              <OnTraccrNumberInput
                min={0}
                value={wageValues[key]}
                onChange={(value) => onWageChange(record.id, key, value)}
                style={{ width: '100%' }}
                prefix={isCurrency ? '$' : undefined}
                precision={isCurrency ? 2 : 4}
                formatter={isCurrency ? INPUT_OPTIONS.formatter : undefined}
                parser={isCurrency ? INPUT_OPTIONS.parser : undefined}
                placeholder={`Enter ${label.toLowerCase()}`}
                disabled={isDisabled}
                className="budget-tracking-display-dropdown"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

WageConfigurationForm.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string,
    categoryIds: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  wageValues: PropTypes.shape({
    hourlyWage: PropTypes.number,
    dailyWage: PropTypes.number,
    wageAdjustment: PropTypes.number,
    wageMultiplier: PropTypes.number,
    hourlyBillingRate: PropTypes.number,
    dailyBillingRate: PropTypes.number,
  }),
  onWageChange: PropTypes.func,
};

WageConfigurationForm.defaultProps = {
  wageValues: {},
  onWageChange: () => {},
};
